/**
 * Prediction Engine V2 — Recency Weights
 *
 * Computes per-race weights for historical data based on the selected algorithm config.
 * Handles cross-season decay and team change detection.
 */

import type { RecencyConfig } from "./types";

// ============================================================================
// Algorithm Configs
// ============================================================================

export const SEASON_OVERVIEW_CONFIG: RecencyConfig = {
  id: "season-overview",
  maxRaces: null, // All current season races
  decayFactor: 0, // Equal weight
  crossSeasonDecay: [1.0, 0.7, 0.3, 0.15],
  teamChangePenalty: 0.6,
};

export const RECENT_FORM_CONFIG: RecencyConfig = {
  id: "recent-form",
  maxRaces: 5,
  decayFactor: 0, // Equal weight within window
  crossSeasonDecay: [1.0, 0.7, 0.3, 0.15],
  teamChangePenalty: 0.6,
};

export const HOT_STREAK_CONFIG: RecencyConfig = {
  id: "hot-streak",
  maxRaces: 2,
  decayFactor: 0, // Equal weight within window
  crossSeasonDecay: [1.0, 0.7, 0.3, 0.15],
  teamChangePenalty: 0.6,
};

export const ADAPTIVE_CONFIG: RecencyConfig = {
  id: "adaptive",
  maxRaces: null, // All available
  decayFactor: 0.3, // Exponential decay: last 2 ≈ 50%
  crossSeasonDecay: [1.0, 0.7, 0.3, 0.15],
  teamChangePenalty: 0.6,
};

export const MOMENTUM_CONFIG: RecencyConfig = {
  id: "momentum",
  maxRaces: null, // All available
  decayFactor: 0.3, // Exponential decay: last 2 ≈ 50%
  crossSeasonDecay: [1.0, 0.7, 0.3, 0.15],
  teamChangePenalty: 0.6,
};

// ============================================================================
// Weight Computation
// ============================================================================

export interface WeightedRace {
  index: number;
  weight: number;
}

/**
 * Compute weights for each race in history, ordered most recent first.
 *
 * @param historyLength - Number of races available
 * @param config - Recency configuration
 * @param currentConstructorSlug - Driver's current constructor (for team change detection)
 * @param constructorPerRace - Constructor slug per race, most recent first (optional)
 */
export function computeWeights(
  historyLength: number,
  config: RecencyConfig,
  currentConstructorSlug?: string,
  constructorPerRace?: (string | undefined)[],
): WeightedRace[] {
  const maxRaces = config.maxRaces ?? historyLength;
  const count = Math.min(historyLength, maxRaces);

  if (count === 0) return [];

  const weights: WeightedRace[] = [];

  for (let i = 0; i < count; i++) {
    // Base weight: exponential decay from most recent (i=0)
    let weight = config.decayFactor > 0 ? Math.exp(-config.decayFactor * i) : 1;

    // Team change penalty: if the driver was on a different team for this race
    if (
      currentConstructorSlug &&
      constructorPerRace?.[i] &&
      constructorPerRace[i] !== currentConstructorSlug
    ) {
      weight *= config.teamChangePenalty;
    }

    weights.push({ index: i, weight });
  }

  // Normalize weights to sum to 1
  const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0);
  if (totalWeight > 0) {
    for (const w of weights) {
      w.weight /= totalWeight;
    }
  }

  return weights;
}

/**
 * Compute a weighted mean from values and weights.
 */
export function weightedMean(
  values: number[],
  weights: WeightedRace[],
): number {
  let sum = 0;
  for (const w of weights) {
    if (w.index < values.length) {
      sum += values[w.index] * w.weight;
    }
  }
  return sum;
}

/**
 * Compute weighted variance.
 */
export function weightedVariance(
  values: number[],
  weights: WeightedRace[],
): number {
  const mean = weightedMean(values, weights);
  let sumSqDiff = 0;
  for (const w of weights) {
    if (w.index < values.length) {
      const diff = values[w.index] - mean;
      sumSqDiff += w.weight * diff * diff;
    }
  }
  return sumSqDiff;
}

/**
 * Apply cross-season discount to weights.
 * Races from older seasons receive a heavy penalty — they're far less predictive
 * after rule changes, car development resets, and driver lineup changes.
 *
 * Factor 0.15: previous-season races receive 15% of the weight they would otherwise have.
 * This is aggressive but correct early-season: once current-season races accumulate
 * they'll dominate via recency decay even without the penalty.
 */
export function applySeasonDiscount<T extends { season: number }>(
  weights: WeightedRace[],
  reversed: T[],
  currentSeason: number,
): WeightedRace[] {
  const CROSS_SEASON_FACTOR = 0.15;
  const penalized = weights.map((w) => {
    const race = reversed[w.index];
    if (!race) return w;
    const penalty = race.season < currentSeason ? CROSS_SEASON_FACTOR : 1.0;
    return { ...w, weight: w.weight * penalty };
  });
  const total = penalized.reduce((s, w) => s + w.weight, 0);
  if (total <= 0) return weights;
  return penalized.map((w) => ({ ...w, weight: w.weight / total }));
}

/**
 * Detect if a driver changed teams between their race history and current constructor.
 * Returns the index of the first race with a different constructor (most recent first).
 * Returns -1 if no team change detected.
 */
export function detectTeamChangeIndex(
  constructorPerRace: (string | undefined)[],
  currentConstructorSlug: string,
): number {
  for (let i = 0; i < constructorPerRace.length; i++) {
    if (
      constructorPerRace[i] &&
      constructorPerRace[i] !== currentConstructorSlug
    ) {
      return i;
    }
  }
  return -1;
}
